export const NOTE_ATTACHMENT_LIMITS = {
  maxImages: 5,
  maxImageBytes: 10 * 1024 * 1024,
  maxVideoBytes: 200 * 1024 * 1024,
  maxDocuments: 5,
  maxDocumentBytes: 20 * 1024 * 1024,
};

export const DOCUMENT_TYPES = [
  { extension: '.pdf', mimeType: 'application/pdf' },
  {
    extension: '.docx',
    mimeType:
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  {
    extension: '.xlsx',
    mimeType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
];

export const DOCUMENT_ACCEPT = DOCUMENT_TYPES.flatMap(
  ({ extension, mimeType }) => [extension, mimeType]
).join(',');

export const isDocumentName = (name: string): boolean => {
  const lowerName = name.toLowerCase();
  return DOCUMENT_TYPES.some(({ extension }) => lowerName.endsWith(extension));
};

export const isDocumentMimeType = (mimeType: string): boolean =>
  DOCUMENT_TYPES.some((type) => type.mimeType === mimeType);

export const isDocumentFile = (file: File): boolean =>
  isDocumentMimeType(file.type) || isDocumentName(file.name);

export const isVideoFile = (file: File): boolean =>
  file.type.startsWith('video/');

export const isImageFile = (file: File): boolean =>
  !isVideoFile(file) && !isDocumentFile(file);

const isMediaFile = (file: File): boolean =>
  !isDocumentFile(file) &&
  (!file.type ||
    file.type.startsWith('image/') ||
    file.type.startsWith('video/'));

export interface AttachmentRejection {
  key: string;
  params?: Record<string, string | number>;
}

export interface AttachmentPickResult {
  accepted: File[];
  rejection: AttachmentRejection | null;
}

const bytesToMB = (bytes: number) => Math.round(bytes / 1024 / 1024);

const mixedMediaRejection: AttachmentRejection = {
  key: 'noteCaptureAttachments.photoAndVideoUnsupported',
};

const unsupportedFileRejection: AttachmentRejection = {
  key: 'noteCaptureAttachments.errors.unsupportedFile',
};

export function validateImagePicks(
  picked: File[],
  existing: File[]
): AttachmentPickResult {
  if (!picked.every(isMediaFile)) {
    return { accepted: [], rejection: unsupportedFileRejection };
  }
  if (existing.some(isVideoFile)) {
    return { accepted: [], rejection: mixedMediaRejection };
  }
  const oversized = picked.some(
    (f) => f.size > NOTE_ATTACHMENT_LIMITS.maxImageBytes
  );
  if (oversized) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.imageTooLarge',
        params: { maxMB: bytesToMB(NOTE_ATTACHMENT_LIMITS.maxImageBytes) },
      },
    };
  }
  const existingImages = existing.filter(isImageFile);
  if (
    existingImages.length + picked.length >
    NOTE_ATTACHMENT_LIMITS.maxImages
  ) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.tooManyImages',
        params: { max: NOTE_ATTACHMENT_LIMITS.maxImages },
      },
    };
  }
  return { accepted: picked, rejection: null };
}

export function validateVideoPick(
  picked: File,
  existing: File[] = []
): AttachmentPickResult {
  if (!isMediaFile(picked)) {
    return { accepted: [], rejection: unsupportedFileRejection };
  }
  if (existing.some(isImageFile)) {
    return { accepted: [], rejection: mixedMediaRejection };
  }
  if (picked.size > NOTE_ATTACHMENT_LIMITS.maxVideoBytes) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.videoTooLarge',
        params: { maxMB: bytesToMB(NOTE_ATTACHMENT_LIMITS.maxVideoBytes) },
      },
    };
  }
  return { accepted: [picked], rejection: null };
}

export function validateMixedPicks(
  picked: File[],
  existing: File[]
): AttachmentPickResult {
  if (!picked.every(isMediaFile)) {
    return { accepted: [], rejection: unsupportedFileRejection };
  }
  const video = picked.find(isVideoFile);
  if (video) return validateVideoPick(video, existing);
  return validateImagePicks(picked, existing);
}

export function validateDocumentPicks(
  picked: File[],
  existing: File[]
): AttachmentPickResult {
  if (!picked.every((file) => isDocumentName(file.name))) {
    return { accepted: [], rejection: unsupportedFileRejection };
  }
  const oversized = picked.some(
    (f) => f.size > NOTE_ATTACHMENT_LIMITS.maxDocumentBytes
  );
  if (oversized) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.fileTooLarge',
        params: { maxMB: bytesToMB(NOTE_ATTACHMENT_LIMITS.maxDocumentBytes) },
      },
    };
  }
  const existingDocuments = existing.filter(isDocumentFile);
  if (
    existingDocuments.length + picked.length >
    NOTE_ATTACHMENT_LIMITS.maxDocuments
  ) {
    return {
      accepted: [],
      rejection: {
        key: 'noteCaptureAttachments.errors.tooManyFiles',
        params: { max: NOTE_ATTACHMENT_LIMITS.maxDocuments },
      },
    };
  }
  return { accepted: picked, rejection: null };
}
